/* ============================================================================
   wl-wishlist-sync.js — a saved item leaves the wishlist once it is in the bag.
   ----------------------------------------------------------------------------
   The wishlist is a "still deciding" list. Once a piece is actually in the cart
   the decision is made, so keeping it saved leaves the shopper with two lists
   that disagree and a heart that stays lit on something they already bought.

   Rather than patch every Add-to-Cart button (the wishlist drawer, the wishlist
   page, collection quick-add, the product form, the drawer's recommendations —
   each with its own handler), this watches the ONE thing they all have in
   common: a successful POST to /cart/add.js. Both fetch and XHR are covered
   because the theme uses both.

   Deliberately NOT part of wl-cart-drawer-opt.js: that file is flag-gated and
   returns early when the optimizer is off, which would make this behaviour
   vanish at ?wlopt=0. This is product behaviour, not a performance tweak.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__wlWishSync) return;
  window.__wlWishSync = true;

  var KEY = 'theme4:wishlist:id';

  function ids() {
    try { return (localStorage.getItem(KEY) || '').split(',').filter(Boolean); }
    catch (e) { return []; }
  }

  /* Drop the given product ids from the wishlist.

     PREFER THE THEME'S OWN HEART. custom.js keeps an in-memory copy of the list and
     renders the button label ("Remove Wishlist" / "Add to Wishlist"), the header count
     and the tooltip from it. It does NOT listen for storage changes, so writing
     localStorage behind its back leaves a filled heart on an item that is no longer
     saved -- and worse, the theme would then think the item was never saved and
     re-add it on the next click. Clicking its own control keeps all of that in step.
     Only products with no heart on the page fall back to a direct write. */
  function drop(pids) {
    if (!pids || !pids.length) return;
    var list = ids();
    if (!list.length) return;
    var todo = pids.map(String).filter(function (p) { return list.indexOf(p) > -1; });
    if (!todo.length) return;

    /* DECLARE INTENT BEFORE CLICKING THE HEART.
       `ctrl.click()` below runs custom.js's `action_after_remove_add_fn`, which on the
       wishlist page calls `wlNavOrRefresh()` -> `window.location.href = ...`. That guard
       only stays silent when `window.__wlNoWishNav` is set OR `#CartDrawer` is ALREADY
       open. This removal fires the moment `/cart/add.js` resolves, which is BEFORE the
       drawer has finished opening - so the guard missed, the page navigated, and the
       drawer the shopper had just opened was torn down. Reported 2026-08-01:
       "when i added the product from wishlist product got removed drawer also got closed".
       custom.js already provides the flag; nothing here ever set it. A MANUAL heart click
       on the wishlist page is untouched and still navigates as designed, because the flag
       is only raised around OUR programmatic clicks and released straight after. */
    var wasNoNav = window.__wlNoWishNav;
    window.__wlNoWishNav = true;

    var manual = [];
    try {
      todo.forEach(function (pid) {
        var heart = document.querySelector('hdt-wishlist[data-id="' + pid.replace(/"/g, '') + '"]');
        var ctrl = heart && (heart.querySelector('button, a') || heart);
        if (ctrl && typeof ctrl.click === 'function') {
          try { ctrl.click(); return; } catch (e) {}
        }
        manual.push(pid);
      });

      if (manual.length) {
        var out = ids().filter(function (id) { return manual.indexOf(String(id)) < 0; });
        try { localStorage.setItem(KEY, out.join(',')); } catch (e) {}
      }
      /* our own surfaces (wishlist badge, wishlist drawer, wishlist page) listen for this */
      try {
        document.dispatchEvent(new CustomEvent('theme4:wishlist:update', { bubbles: true, detail: ids() }));
      } catch (e) {}
      dropCards(todo);
    } finally {
      /* Release LATE and on a timer, not synchronously: custom.js queues work off those
         clicks, so clearing the flag in the same tick would let a late handler navigate
         after all. By 1500ms the drawer is open and its own `drawer.open` guard takes
         over. `finally` so a throw above can never leave the flag stuck on. */
      setTimeout(function () { window.__wlNoWishNav = wasNoNav || false; }, 1500);
    }
  }

  /* On the wishlist VIEW the grid is server-rendered from a search query, so clearing
     storage alone leaves the card sitting there — the item looks like it is still
     saved. Take the card out too. Scoped to view=wishlist so a collection or search
     page never loses a card just because the shopper added it to the bag.

     DO NOT `location.reload()` WHEN THE LAST CARD GOES. That used to run here and caused
     two shopper-visible bugs at once (reported 2026-08-01, "drawer opens and then closes
     and the wishlist count is 0 but I could still see the product"):
       1. adding to cart opens the drawer, then the reload 300ms later TORE IT DOWN;
       2. the wishlist URL carries the old query (`?view=wishlist&q=id:123 OR id:456`), so
          the reloaded page re-rendered THAT PRODUCT from the query even though the
          wishlist was now empty - count 0, card back on screen.
     Its "any cards left" test was wrong anyway: `document.querySelector('.hdt-card-product')`
     matches recently-viewed and recommendation cards too (measured 22 on a wishlist page
     holding ONE item), so it was never a reliable end-of-grid signal.
     The empty state is already handled properly by `refresh()` in
     sections/main-wishlist.liquid, which this function's own
     `theme4:wishlist:update` dispatch (above) triggers: with no ids left it empties the
     grid and reveals [data-wl-wishempty] in place, no navigation. */
  function onWishlistView() {
    try { return new URLSearchParams(location.search).get('view') === 'wishlist'; }
    catch (e) { return false; }
  }
  function dropCards(pids) {
    if (!onWishlistView()) return;
    var removed = 0;
    pids.forEach(function (p) {
      var hearts = document.querySelectorAll('hdt-wishlist[data-id="' + String(p).replace(/"/g, '') + '"]');
      [].forEach.call(hearts, function (h) {
        var card = h.closest('.hdt-card-product') || h.closest('hdt-card-product');
        if (!card || card.__wlGoing) return;
        card.__wlGoing = true;
        removed++;
        card.style.transition = 'opacity .28s ease, transform .28s ease';
        card.style.opacity = '0';
        card.style.transform = 'scale(.97)';
        setTimeout(function () {
          if (card.parentNode) card.parentNode.removeChild(card);
        }, 300);
      });
    });
    return removed;
  }

  /* /cart/add.js answers with the single line it added, or {items:[...]} for a
     multi-add. Anything else (an error payload) simply yields no ids. */
  function pidsFrom(data) {
    if (!data || typeof data !== 'object') return [];
    if (data.items && data.items.length) {
      return data.items.map(function (i) { return i && i.product_id; }).filter(Boolean);
    }
    return data.product_id ? [data.product_id] : [];
  }

  function isAddUrl(url) {
    try { return /\/cart\/add(\.js)?(\?|$)/.test(String(url || '').split('#')[0]); }
    catch (e) { return false; }
  }

  /* ---- fetch ------------------------------------------------------------- */
  var _fetch = window.fetch;
  if (typeof _fetch === 'function') {
    window.fetch = function (input, init) {
      var url = (input && input.url) || input;
      var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      var watch = method === 'POST' && isAddUrl(url);
      var p = _fetch.apply(this, arguments);
      if (!watch) return p;
      return p.then(function (res) {
        if (!res || !res.ok) return res;
        /* clone so the caller still gets an unread body */
        try {
          res.clone().json().then(function (d) { drop(pidsFrom(d)); }).catch(function () {});
        } catch (e) {}
        return res;
      });
    };
  }

  /* ---- XMLHttpRequest ---------------------------------------------------- */
  var XHR = window.XMLHttpRequest;
  if (XHR && XHR.prototype) {
    var _open = XHR.prototype.open;
    var _send = XHR.prototype.send;
    XHR.prototype.open = function (method, url) {
      this.__wlAdd = String(method || '').toUpperCase() === 'POST' && isAddUrl(url);
      return _open.apply(this, arguments);
    };
    XHR.prototype.send = function () {
      if (this.__wlAdd) {
        this.addEventListener('load', function () {
          if (this.status < 200 || this.status >= 300) return;
          var d = null;
          try { d = typeof this.response === 'object' ? this.response : JSON.parse(this.responseText); }
          catch (e) { return; }
          drop(pidsFrom(d));
        });
      }
      return _send.apply(this, arguments);
    };
  }
})();
